import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import RolesTab from '../components/roles/RolesTab';
import UsersTab from '../components/users/UsersTab';
import UserRolesPanel from '../components/users/UserRolesPanel';
import AuditTab from '../components/audit/AuditTab';
import useCan from '../hooks/useCan';

/**
 * «Ruoli e permessi» (spec §10.1).
 *
 * I tab sono tre — Ruoli, Utenti e Audit log. Il tab Gruppi è esistito fra la Fase 4A e
 * questa specifica: i ruoli potevano arrivare anche da un gruppo Keycloak, e quella era una
 * seconda porta d'ingresso all'autorizzazione che Automate governava solo a metà. Oggi i
 * ruoli si assegnano da un posto solo, il tab Utenti.
 *
 * Ciascun tab si rende solo se il permesso che lo abilita e' concesso — `roles.read` per
 * Ruoli, `users.read` per Utenti, `audit.read` per Audit log — cosi' un domani in cui i
 * permessi divergessero fra ruoli diversi la pagina si comporterebbe da sola, senza che
 * qualcuno se ne ricordi.
 *
 * Il tab iniziale e' il primo disponibile fra quelli che i permessi correnti ammettono
 * (ordine Ruoli, Utenti, Audit log), a meno che `?tab=` dalla query string ne indichi uno
 * gia' ammesso: un parametro sopravvive a un ricaricamento della pagina, a differenza di
 * uno `state` di navigazione, e serve a chi arriva da un collegamento come «cambiate dal
 * tab Utenti» in `RoleUsersPanel`, che deve aprire qui gia' sul tab giusto. Con `D9` la
 * guardia di rotta ammette chiunque abbia almeno uno dei tre permessi, quindi un revisore
 * con solo `audit.read` deve trovare qui il proprio tab aperto, non una pagina vuota
 * (§10.5).
 */
export default function RolesAndPermissions() {
    const can = useCan();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const canReadRoles = can('roles.read');
    const canReadUsers = can('users.read');
    const canReadAudit = can('audit.read');
    const tabDaQuery = searchParams.get('tab');
    const disponibili = [
        canReadRoles && 'ruoli',
        canReadUsers && 'utenti',
        canReadAudit && 'audit',
    ].filter(Boolean);
    const tabIniziale = disponibili.includes(tabDaQuery) ? tabDaQuery : disponibili[0];
    const [tab, setTab] = useState(tabIniziale);
    const [selected, setSelected] = useState(null);
    // Il pannello delle concessioni cambia l'utente fuori dalla tabella: se ha salvato
    // qualcosa, alla chiusura la tabella si ricarica, altrimenti mostrerebbe i ruoli di
    // prima. Alla chiusura e non a ogni salvataggio: la riga sotto la finestra non si vede.
    const [salvatoNelPannello, setSalvatoNelPannello] = useState(false);
    const [aggiornamentoUtenti, setAggiornamentoUtenti] = useState(0);

    const chiudiPannello = () => {
        setSelected(null);
        if (salvatoNelPannello) {
            setSalvatoNelPannello(false);
            setAggiornamentoUtenti((n) => n + 1);
        }
    };

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Ruoli e permessi
            </Typography>

            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
                {canReadRoles && <Tab value="ruoli" label="Ruoli" />}
                {canReadUsers && <Tab value="utenti" label="Utenti" />}
                {canReadAudit && <Tab value="audit" label="Audit log" />}
            </Tabs>

            {tab === 'ruoli' && canReadRoles && (
                <RolesTab onOpenRole={(id) => navigate(`/roles-and-permissions/roles/${id}`)} />
            )}

            {tab === 'utenti' && canReadUsers && (
                <>
                    <UsersTab onSelectUser={setSelected} aggiornamento={aggiornamentoUtenti} />
                    {selected && (
                        <UserRolesPanel
                            sub={selected}
                            onClose={chiudiPannello}
                            onSaved={() => setSalvatoNelPannello(true)}
                        />
                    )}
                </>
            )}

            {tab === 'audit' && canReadAudit && <AuditTab />}
        </Box>
    );
}
